import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth-context';
import { WorkspaceLayout } from '@/components/layout/WorkspaceLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  ArrowLeft,
  Save,
  ClipboardCheck,
  Settings,
  FileQuestion,
  Shield,
  Eye,
  Plus,
  Trash2,
  GripVertical,
  Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { categoryAPI, courseAPI } from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const typeLabels = {
  single_choice: 'QCM unique',
  multiple_choice: 'QCM multiple',
  true_false: 'Vrai/Faux',
  short_answer: 'Réponse courte',
  long_answer: 'Réponse longue',
  matching: 'Appariement',
  ordering: 'Classement',
};

export default function ExamEditorPage() {
  const { workspaceId, examId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !examId || examId === 'new';
  
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    category_id: '',
    certification: '',
    level: '',
    linked_course_id: '',
    is_visible: true,
    tags: [],
    question_ids: [],
    settings: {
      duration_minutes: 60,
      passing_score: 50,
      total_questions: null,
      randomize_questions: false,
      randomize_choices: false,
      show_results_immediately: true,
      allow_review: true,
      max_attempts: 1,
      enable_proctoring: true,
      max_violations: 3,
      auto_submit_on_violation: false,
      auto_ai_grading: false,
    },
  });
  
  // Available questions
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [questionSearch, setQuestionSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [wsCategories, setWsCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [activeBankCategory, setActiveBankCategory] = useState('all');
  
  useEffect(() => {
    const init = async () => {
      if (workspaceId) {
        await loadCategories(workspaceId);
        await loadQuestions(workspaceId);
        categoryAPI.list(workspaceId).then(res => setWsCategories(res.data || [])).catch(() => {});
        courseAPI.list(workspaceId).then(res => setCourses(res.data || [])).catch(() => {});
        
        if (!isNew) {
          await loadExam(workspaceId);
        } else {
          setLoading(false);
        }
      }
    };
    
    init();
  }, [workspaceId, examId]);
  
  const loadExam = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/${examId}?workspace_id=${wsId}`);
      if (response.ok) {
        const exam = await response.json();
        setFormData({
          title: exam.title || '',
          description: exam.description || '',
          category: exam.category || '',
          category_id: exam.category_id || '',
          certification: exam.certification || '',
          level: exam.level || '',
          linked_course_id: exam.linked_course_id || '',
          is_visible: exam.is_visible !== false,
          tags: exam.tags || [],
          question_ids: exam.question_ids || [],
          settings: {
            duration_minutes: exam.settings?.duration_minutes || 60,
            passing_score: exam.settings?.passing_score || 50,
            total_questions: exam.settings?.total_questions || null,
            randomize_questions: exam.settings?.randomize_questions || false,
            randomize_choices: exam.settings?.randomize_choices || false,
            show_results_immediately: exam.settings?.show_results_immediately ?? true,
            allow_review: exam.settings?.allow_review ?? true,
            max_attempts: exam.settings?.max_attempts || 1,
            enable_proctoring: exam.settings?.enable_proctoring ?? true,
            max_violations: exam.settings?.max_violations || 3,
            auto_submit_on_violation: exam.settings?.auto_submit_on_violation || false,
            auto_ai_grading: exam.settings?.auto_ai_grading || false,
          },
        });
      } else {
        toast.error('Examen non trouvé');
        navigate(`/workspace/${workspaceId}/exams`);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
      navigate(`/workspace/${workspaceId}/exams`);
    } finally {
      setLoading(false);
    }
  };
  
  const loadQuestions = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions?workspace_id=${wsId}&limit=200`);
      const questions = await response.json();
      setAvailableQuestions(questions);
    } catch (error) {
      console.error('Failed to load questions:', error);
    }
  };
  
  const loadCategories = async (wsId) => {
    try {
      const response = await fetch(`${API_URL}/api/exams/questions/categories/list?workspace_id=${wsId}`);
      const cats = await response.json();
      setCategories(cats || []);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };
  
  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    
    setSaving(true);
    try {
      const url = isNew 
        ? `${API_URL}/api/exams/?workspace_id=${workspaceId}`
        : `${API_URL}/api/exams/${examId}?workspace_id=${workspaceId}`;
      
      const method = isNew ? 'POST' : 'PUT';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(isNew ? 'Examen créé' : 'Examen mis à jour');
        
        if (isNew) {
          navigate(`/workspace/${workspaceId}/exams/${result.id}/edit`);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const toggleQuestion = (questionId) => {
    setFormData((prev) => {
      const ids = prev.question_ids.includes(questionId)
        ? prev.question_ids.filter((id) => id !== questionId)
        : [...prev.question_ids, questionId];
      return { ...prev, question_ids: ids };
    });
  };
  
  const removeQuestion = (questionId) => {
    setFormData((prev) => ({
      ...prev,
      question_ids: prev.question_ids.filter((id) => id !== questionId),
    }));
  };
  
  const updateSettings = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      settings: { ...prev.settings, [key]: value },
    }));
  };
  
  const filteredQuestions = availableQuestions.filter((q) => {
    // Search filter
    if (questionSearch && !q.question_text.toLowerCase().includes(questionSearch.toLowerCase())) {
      return false;
    }
    // Category filter
    if (activeBankCategory !== 'all') {
      const qCat = q.category
        ? (typeof q.category === 'object' ? q.category.name : q.category)
        : 'Sans catégorie';
      if (qCat !== activeBankCategory) return false;
    }
    return true;
  });
  
  // Get unique categories from questions
  const bankCategories = [...new Set(availableQuestions.map(q => {
    if (!q.category) return 'Sans catégorie';
    return typeof q.category === 'object' ? q.category.name : q.category;
  }))].sort();
  
  const selectAllVisible = () => {
    const visibleIds = filteredQuestions.map(q => q.id);
    setFormData(prev => ({
      ...prev,
      question_ids: [...new Set([...prev.question_ids, ...visibleIds])],
    }));
  };
  
  const deselectAllVisible = () => {
    const visibleIds = new Set(filteredQuestions.map(q => q.id));
    setFormData(prev => ({
      ...prev,
      question_ids: prev.question_ids.filter(id => !visibleIds.has(id)),
    }));
  };
  
  const allVisibleSelected = filteredQuestions.length > 0 && 
    filteredQuestions.every(q => formData.question_ids.includes(q.id));
  
  const selectedQuestions = availableQuestions.filter((q) => 
    formData.question_ids.includes(q.id)
  );
  
  const totalPoints = selectedQuestions.reduce((sum, q) => sum + (q.points || 1), 0);
  
  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </WorkspaceLayout>
    );
  }
  
  return (
    <WorkspaceLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/workspace/${workspaceId}/exams`)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">
                {isNew ? 'Nouvel examen' : 'Modifier l\'examen'}
              </h1>
              <p className="text-muted-foreground">
                {selectedQuestions.length} question(s) • {totalPoints} points
              </p>
            </div>
          </div>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate(`/workspace/${workspaceId}/exams`)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="info" className="flex items-center gap-2">
              <ClipboardCheck className="h-4 w-4" />
              Informations
            </TabsTrigger>
            <TabsTrigger value="questions" className="flex items-center gap-2">
              <FileQuestion className="h-4 w-4" />
              Questions ({selectedQuestions.length})
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="h-4 w-4" />
              Paramètres
            </TabsTrigger>
            <TabsTrigger value="proctoring" className="flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Anti-triche
            </TabsTrigger>
          </TabsList>
          
          {/* Info Tab */}
          <TabsContent value="info" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Informations générales</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre de l'examen *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Certification Docker Niveau 1"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Décrivez le contenu et les objectifs de l'examen..."
                    rows={3}
                  />
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="category">Catégorie</Label>
                    <Select
                      value={formData.category_id || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, category_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucune</SelectItem>
                        {wsCategories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span className="w-3 h-3 rounded" style={{ backgroundColor: cat.color }} />
                              {cat.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="certification">Certification</Label>
                    <Input
                      id="certification"
                      value={formData.certification}
                      onChange={(e) => setFormData({ ...formData, certification: e.target.value })}
                      placeholder="Ex: Docker Essentials"
                    />
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="level">Niveau</Label>
                    <Select
                      value={formData.level || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, level: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Non défini</SelectItem>
                        <SelectItem value="Débutant">Débutant</SelectItem>
                        <SelectItem value="Intermédiaire">Intermédiaire</SelectItem>
                        <SelectItem value="Avancé">Avancé</SelectItem>
                        <SelectItem value="Expert">Expert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="linked_course">Cours associé</Label>
                    <Select
                      value={formData.linked_course_id || 'none'}
                      onValueChange={(v) => setFormData({ ...formData, linked_course_id: v === 'none' ? '' : v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner un cours..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Aucun cours</SelectItem>
                        {courses.map((course) => (
                          <SelectItem key={course.id} value={course.id}>
                            <span className="flex items-center gap-2">
                              {course.color && (
                                <span className="w-3 h-3 rounded" style={{ backgroundColor: course.color }} />
                              )}
                              {course.title || course.name}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Visibility toggle */}
                <div className="flex items-center justify-between rounded-lg border p-4 mt-4">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Visible pour les candidats</Label>
                    <p className="text-xs text-muted-foreground">
                      {formData.is_visible
                        ? "Les candidats peuvent voir et passer cet examen"
                        : "Cet examen est masqué pour les candidats"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                  />
                </div>

              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Questions Tab */}
          <TabsContent value="questions" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Selected Questions */}
              <Card>
                <CardHeader>
                  <CardTitle>Questions sélectionnées ({selectedQuestions.length})</CardTitle>
                  <CardDescription>
                    Total : {totalPoints} points
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {selectedQuestions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileQuestion className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Aucune question sélectionnée</p>
                      <p className="text-sm">Ajoutez des questions depuis la liste</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[500px] overflow-y-auto">
                      {selectedQuestions.map((q, index) => (
                        <div
                          key={q.id}
                          className="flex items-start gap-3 p-3 bg-muted rounded-lg group"
                        >
                          <GripVertical className="h-5 w-5 text-muted-foreground mt-0.5 cursor-move" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {index + 1}. {q.question_text?.replace(/<[^>]*>/g, '')}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {typeLabels[q.type] || q.type}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {q.points} pt{q.points > 1 ? 's' : ''}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="opacity-0 group-hover:opacity-100 h-8 w-8"
                            onClick={() => removeQuestion(q.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Available Questions */}
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle>Banque de questions</CardTitle>
                    <Button
                      variant={allVisibleSelected ? 'outline' : 'default'}
                      size="sm"
                      onClick={allVisibleSelected ? deselectAllVisible : selectAllVisible}
                      disabled={filteredQuestions.length === 0}
                    >
                      {allVisibleSelected ? 'Désélectionner tout' : 'Sélectionner tout'}
                    </Button>
                  </div>
                  <div className="relative mt-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Rechercher..."
                      value={questionSearch}
                      onChange={(e) => setQuestionSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {/* Category tabs */}
                  {bankCategories.length > 1 && (
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      <button
                        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                          activeBankCategory === 'all'
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                        onClick={() => setActiveBankCategory('all')}
                      >
                        Toutes ({availableQuestions.length})
                      </button>
                      {bankCategories.map(cat => {
                        const count = availableQuestions.filter(q => {
                          const qCat = q.category
                            ? (typeof q.category === 'object' ? q.category.name : q.category)
                            : 'Sans catégorie';
                          return qCat === cat;
                        }).length;
                        return (
                          <button
                            key={cat}
                            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                              activeBankCategory === cat
                                ? 'bg-primary text-primary-foreground'
                                : 'bg-muted text-muted-foreground hover:bg-muted/80'
                            }`}
                            onClick={() => setActiveBankCategory(cat)}
                          >
                            {cat} ({count})
                          </button>
                        );
                      })}
                    </div>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {filteredQuestions.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <p>Aucune question disponible</p>
                        <Button
                          variant="link"
                          onClick={() => navigate(`/workspace/${workspaceId}/exams/questions/new`)}
                        >
                          Créer une question
                        </Button>
                      </div>
                    ) : (
                      filteredQuestions.map((q) => {
                        const isSelected = formData.question_ids.includes(q.id);
                        return (
                          <div
                            key={q.id}
                            className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected ? 'bg-primary/5 border-primary' : 'hover:bg-muted'
                            }`}
                            onClick={() => toggleQuestion(q.id)}
                          >
                            <Checkbox checked={isSelected} className="mt-1" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm truncate">{q.question_text?.replace(/<[^>]*>/g, '')}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {typeLabels[q.type] || q.type}
                                </Badge>
                                {q.category && (
                                  <Badge variant="secondary" className="text-xs">
                                    {q.category}
                                  </Badge>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {q.points} pt{q.points > 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Settings Tab */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Paramètres de l'examen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Durée (minutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.settings.duration_minutes}
                      onChange={(e) => updateSettings('duration_minutes', parseInt(e.target.value) || 60)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Score minimum pour réussir (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={formData.settings.passing_score}
                      onChange={(e) => updateSettings('passing_score', parseInt(e.target.value) || 50)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Nombre de questions (vide = toutes)</Label>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Toutes les questions"
                      value={formData.settings.total_questions || ''}
                      onChange={(e) => updateSettings('total_questions', e.target.value ? parseInt(e.target.value) : null)}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tentatives maximum</Label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.settings.max_attempts}
                      onChange={(e) => updateSettings('max_attempts', parseInt(e.target.value) || 1)}
                    />
                  </div>
                </div>
                
                <div className="space-y-4 pt-4 border-t">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mélanger les questions</Label>
                      <p className="text-sm text-muted-foreground">
                        L'ordre des questions sera aléatoire
                      </p>
                    </div>
                    <Switch
                      checked={formData.settings.randomize_questions}
                      onCheckedChange={(v) => updateSettings('randomize_questions', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Mélanger les choix</Label>
                      <p className="text-sm text-muted-foreground">
                        L'ordre des réponses QCM sera aléatoire
                      </p>
                    </div>
                    <Switch
                      checked={formData.settings.randomize_choices}
                      onCheckedChange={(v) => updateSettings('randomize_choices', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Afficher les résultats immédiatement</Label>
                      <p className="text-sm text-muted-foreground">
                        Le candidat voit son score après soumission
                      </p>
                    </div>
                    <Switch
                      checked={formData.settings.show_results_immediately}
                      onCheckedChange={(v) => updateSettings('show_results_immediately', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label>Autoriser la révision</Label>
                      <p className="text-sm text-muted-foreground">
                        Le candidat peut voir les corrections détaillées
                      </p>
                    </div>
                    <Switch
                      checked={formData.settings.allow_review}
                      onCheckedChange={(v) => updateSettings('allow_review', v)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="flex items-center gap-2">Correction IA Automatique</Label>
                      <p className="text-sm text-muted-foreground">
                        Corriger automatiquement les questions textuelles avec l'IA à la soumission
                      </p>
                    </div>
                    <Switch
                      checked={formData.settings.auto_ai_grading || false}
                      onCheckedChange={(v) => updateSettings('auto_ai_grading', v)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Proctoring Tab */}
          <TabsContent value="proctoring" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Surveillance anti-triche</CardTitle>
                <CardDescription>
                  Configurez les mesures de protection contre la triche
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Activer la surveillance</Label>
                    <p className="text-sm text-muted-foreground">
                      Détecte les changements d'onglet, copier/coller, etc.
                    </p>
                  </div>
                  <Switch
                    checked={formData.settings.enable_proctoring}
                    onCheckedChange={(v) => updateSettings('enable_proctoring', v)}
                  />
                </div>
                
                {formData.settings.enable_proctoring && (
                  <>
                    <div className="space-y-2">
                      <Label>Nombre maximum d'infractions</Label>
                      <Input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.settings.max_violations}
                        onChange={(e) => updateSettings('max_violations', parseInt(e.target.value) || 3)}
                        className="w-32"
                      />
                      <p className="text-sm text-muted-foreground">
                        Avertissements avant action
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <Label>Soumission automatique</Label>
                        <p className="text-sm text-muted-foreground">
                          Soumettre l'examen automatiquement après le maximum d'infractions
                        </p>
                      </div>
                      <Switch
                        checked={formData.settings.auto_submit_on_violation}
                        onCheckedChange={(v) => updateSettings('auto_submit_on_violation', v)}
                      />
                    </div>
                    
                    <div className="bg-muted p-4 rounded-lg space-y-2">
                      <h4 className="font-medium">Protections activées :</h4>
                      <ul className="text-sm text-muted-foreground space-y-1">
                        <li>• Détection du changement d'onglet</li>
                        <li>• Détection de la perte de focus</li>
                        <li>• Blocage du clic droit</li>
                        <li>• Blocage de copier/coller</li>
                        <li>• Blocage des raccourcis clavier (Ctrl+C, Ctrl+V, F12)</li>
                        <li>• Incitation au mode plein écran</li>
                      </ul>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </WorkspaceLayout>
  );
}
